'use client'

import { useState, useCallback } from 'react'
import { ChevronDown } from '@/components/ui/icons'
import { useExtracted } from 'next-intl'

export interface PricingFAQItem {
  id: string
  question: string
  answer: string
}

interface PricingFAQProps {
  faqs: PricingFAQItem[]
}

export function PricingFAQ({ faqs }: PricingFAQProps) {
  const t = useExtracted()
  const [openId, setOpenId] = useState<string | null>(null)

  const toggleFAQ = useCallback((id: string) => {
    setOpenId(current => (current === id ? null : id))
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, id: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggleFAQ(id)
      }
    },
    [toggleFAQ],
  )

  return (
    <section className="py-12" aria-labelledby="pricing-faq-heading">
      <h2 id="pricing-faq-heading" className="mb-8 text-3xl font-bold tracking-tight md:text-4xl">
        {t('Frequently Asked Questions')}
      </h2>
      <div className="space-y-3" role="list">
        {faqs.map(faq => {
          const isOpen = openId === faq.id
          return (
            <div
              key={faq.id}
              id={faq.id}
              className="rounded-xl border border-white/10 bg-white/[0.02] transition-colors"
              role="listitem"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                onKeyDown={e => handleKeyDown(e, faq.id)}
                aria-expanded={isOpen}
                aria-controls={`${faq.id}-content`}
                className="flex w-full cursor-pointer items-center justify-between px-6 py-4 text-start text-base font-medium text-white md:text-lg"
              >
                <span className="pe-4">{faq.question}</span>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              <div
                id={`${faq.id}-content`}
                role="region"
                aria-labelledby={faq.id}
                className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              >
                <div className="overflow-hidden">
                  <p className="px-6 pb-4 text-sm leading-relaxed text-white/70 md:text-base">{faq.answer}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
